import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useInView } from 'framer-motion'
import { ResponsiveObject } from 'react-slick'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { AsH1, TitleLG } from '@/atoms/Text'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { HorizontalItemScroller } from '@/molecules/HorizontalItemScroller'
import { PlaceholderMediaCard } from '@/molecules/MediaCard'
import { NumberedPosterLink } from '@/page/CollectionDisplay/NumberedPosterLink'
import { getWebClient } from '@/services/ApolloClient'
import { ContentCollectionEdge, getContentCollections } from '@/services/CollectionsService'
import { LinkViewModel } from '@/services/RenderService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'

const defaultResponsiveSlick: ResponsiveObject[] = [
  {
    breakpoint: 1536,
    settings: {
      slidesToScroll: 5,
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 1280,
    settings: {
      slidesToScroll: 4,
      slidesToShow: 4,
    },
  },
  {
    breakpoint: 1024,
    settings: {
      slidesToScroll: 3,
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 768,
    settings: {
      slidesToScroll: 2,
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 640,
    settings: {
      slidesToScroll: 2,
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 480,
    settings: {
      slidesToScroll: 2,
      slidesToShow: 2,
    },
  },
]

export const CollectionRail: React.FC<{
  landing?: boolean
  title: string
  collectionId: string
  showNumberedRail?: boolean
  isGuildMember?: boolean
  loading?: boolean
}> = ({ landing = false, title, collectionId, showNumberedRail, isGuildMember, loading }) => {
  const { locale } = useLocale()
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true })
  const client = getWebClient({ locale })
  const [isLoaded, setIsLoaded] = useState(false)
  const [collectionProjects, setCollectionProjects] = useState<ContentCollectionEdge[]>([])

  useEffect(() => {
    if (isLoaded || !inView) return

    const fetchCollectionProjects = async () => {
      try {
        const response = await getContentCollections([collectionId], client)
        const definedProjects = response?.[0]?.connection?.edges?.filter(isDefined)

        setCollectionProjects(definedProjects ?? [])
      } catch (error) {
        logger().warn('Error fetching collection projects on Collection Rail', { collectionId }, error)
      } finally {
        setIsLoaded(true)
      }
    }

    fetchCollectionProjects()
  }, [isLoaded, inView, collectionId, client])

  if (isLoaded && !collectionProjects?.length) return null

  return (
    <div ref={ref}>
      <header>
        <TitleLG
          color="white"
          weight="bold"
          as={AsH1}
          className={classNames('mb-3 md:mb-4', { 'photon-title-md !mb-8': landing })}
        >
          {title}
        </TitleLG>
      </header>

      <HorizontalItemScroller slidesToShow={4} slidesToScroll={4} responsive={defaultResponsiveSlick} arrows>
        {!isLoaded
          ? Array(7)
              .fill(0)
              .map((_v, i) =>
                showNumberedRail ? (
                  <PosterShell key={i}>
                    <div aria-hidden="true" className="h-full w-full animate-pulse rounded-lg bg-core-gray-800/70" />
                  </PosterShell>
                ) : (
                  <PlaceholderMediaCard
                    className="mr-4"
                    borderRadiusClassName="rounded-lg"
                    hideTextPlaceholders
                    orientation="portrait"
                    key={i}
                  />
                ),
              )
          : collectionProjects?.map((item: ContentCollectionEdge, index) => {
              if (!item || !item?.node) return null
              const image =
                item?.node?.portraitAngelImage?.cloudinaryPath ?? item?.node?.posterTitleImage?.cloudinaryPath
              if (!image) return null

              const title = item?.node?.name ?? ''
              const link = mapCollectionEdgeToLinkViewModel(item)
              const key = item?.node?.id ?? `${title}-${index}`

              return showNumberedRail && link ? (
                <PosterShell key={key}>
                  <div className="h-full w-full">
                    <NumberedPosterLink link={link} isGuildMember={isGuildMember} rank={index + 1} loading={loading} />
                  </div>
                </PosterShell>
              ) : (
                <InternalLink href={link?.linkUrl ?? '#'} key={key}>
                  <div className="mb-2 mr-4">
                    <div className="block h-fit">
                      <Image
                        aria-label={title}
                        alt={title}
                        loading="lazy"
                        src={getCloudinaryImageUrl({
                          path: image,
                          width: 300,
                          height: 450,
                        })}
                        height={450}
                        width={300}
                        className="rounded-lg bg-black"
                      />
                    </div>
                  </div>
                  {!landing && (
                    <TitleAndSubtitleDisplay subtitleClassName="!text-core-gray-300" className="mr-4" title={title} />
                  )}
                </InternalLink>
              )
            })}
      </HorizontalItemScroller>
    </div>
  )
}

function mapCollectionEdgeToLinkViewModel(edge: ContentCollectionEdge): LinkViewModel | undefined {
  if (!edge?.node) return
  const node = edge.node

  const portrait = node.portraitAngelImage?.cloudinaryPath || node.posterTitleImage?.cloudinaryPath || null
  if (!portrait) return

  const slug = ('project' in node && node?.project?.slug) || ''

  const linkUrl = slug ? `/watch/${slug}` : '#'

  return {
    id: (node as unknown as { id?: string })?.id,
    projectSlug: slug,
    name: node.name ?? '',
    imageUrl: portrait ? getCloudinaryImageUrl({ path: portrait, width: 300, height: 450 }) : undefined,
    portraitUrl: portrait ? getCloudinaryImageUrl({ path: portrait, width: 300, height: 450 }) : undefined,
    linkUrl,
    alt: node.name ?? '',
    projectType: (node as unknown as { projectType?: string })?.projectType,
  }
}

const PosterShell: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="mb-2 mr-4">
    <div className="aspect-[2/3] relative w-full">{children}</div>
  </div>
)
